import { useState } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <nav className="fixed top-0 left-0 w-full z-50 transition-all duration-300 bg-background-50/80 backdrop-blur-md border-b border-background-200/60">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2.5 cursor-pointer">
            <div className="w-8 h-8 rounded-lg bg-primary-500 flex items-center justify-center">
              <i className="ri-file-list-3-line text-background-50 text-sm"></i>
            </div>
            <span className="font-heading text-lg font-semibold text-foreground-900 tracking-tight">
              Sugargoo Sheet
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-6">
            <LinkItem to="/" icon="ri-home-4-line" label="Home" />
            <LinkItem to="#categories" icon="" label="Categories" />
            <LinkItem to="#resources" icon="" label="Resources" />
            <LinkItem to="#articles" icon="" label="Guides" />
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap"
            >
              Sugargoo Spreadsheet
              <i className="ri-arrow-right-up-line text-xs"></i>
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-primary-300 text-primary-600 text-sm font-semibold hover:bg-primary-50 transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-shopping-bag-3-line text-xs"></i>
              Buy It Now
            </a>
          </div>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
            aria-label="Toggle menu"
          >
            <i className={`text-lg ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>

        {mobileOpen && (
          <div className="md:hidden py-4 border-t border-background-200/60 flex flex-col gap-1 max-h-[70vh] overflow-y-auto">
            <MobileLink to="/" icon="ri-home-4-line" label="Home" onClick={() => setMobileOpen(false)} />
            <MobileLink to="#categories" icon="ri-grid-line" label="Categories" onClick={() => setMobileOpen(false)} />
            <MobileLink to="#resources" icon="ri-stack-line" label="Resources" onClick={() => setMobileOpen(false)} />
            <MobileLink to="#articles" icon="ri-article-line" label="Guides & Articles" onClick={() => setMobileOpen(false)} />

            <div className="my-2 mx-2 border-t border-background-200/40"></div>

            <p className="text-xs font-semibold text-foreground-400 uppercase tracking-wide px-2 py-1">Product Categories</p>
            {categories.map((cat) => (
              <a
                key={cat.name}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-3 px-2 py-2 rounded-lg text-sm font-medium text-foreground-600 hover:text-primary-500 hover:bg-background-100 transition-colors cursor-pointer"
              >
                <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-background-200/60 flex-shrink-0">
                  <i className={`${cat.icon} text-xs text-foreground-500`}></i>
                </div>
                {cat.displayName}
              </a>
            ))}

            <div className="my-2 mx-2 border-t border-background-200/40"></div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-3 px-2 py-3 mx-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all cursor-pointer justify-center whitespace-nowrap"
            >
              <i className="ri-arrow-right-up-line text-xs"></i>
              Sugargoo Spreadsheet
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-3 px-2 py-3 mx-2 rounded-full border border-primary-300 text-primary-600 text-sm font-semibold hover:bg-primary-50 transition-all cursor-pointer justify-center whitespace-nowrap"
            >
              <i className="ri-shopping-bag-3-line text-xs"></i>
              Buy It Now
            </a>
          </div>
        )}
      </div>
    </nav>
  );
}

function LinkItem({ to, icon, label }: { to: string; icon: string; label: string }) {
  if (to.startsWith('/') && !to.startsWith('/#')) {
    return (
      <Link to={to} className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground-600 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer">
        {icon && <i className={`${icon} text-xs`}></i>}
        {label}
      </Link>
    );
  }
  return (
    <a href={to} className={`inline-flex items-center gap-1.5 text-sm font-medium text-foreground-600 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer ${!icon ? '' : ''}`}>
      {icon && <i className={`${icon} text-xs`}></i>}
      {label}
    </a>
  );
}

function MobileLink({ to, icon, label, onClick }: { to: string; icon: string; label: string; onClick: () => void }) {
  if (to.startsWith('/') && !to.startsWith('/#')) {
    return (
      <Link
        to={to}
        onClick={onClick}
        className="flex items-center gap-3 px-2 py-2.5 rounded-lg text-sm font-medium text-foreground-600 hover:text-primary-500 hover:bg-background-100 transition-colors cursor-pointer"
      >
        <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-background-200/60 flex-shrink-0">
          <i className={`${icon} text-xs text-foreground-500`}></i>
        </div>
        {label}
      </Link>
    );
  }
  return (
    <a
      href={to}
      onClick={onClick}
      className="flex items-center gap-3 px-2 py-2.5 rounded-lg text-sm font-medium text-foreground-600 hover:text-primary-500 hover:bg-background-100 transition-colors cursor-pointer"
    >
      <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-background-200/60 flex-shrink-0">
        <i className={`${icon} text-xs text-foreground-500`}></i>
      </div>
      {label}
    </a>
  );
}